import { motion } from 'motion/react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCompactViewport } from '@/components/motion'
import { GeoMark, type GeoVariant } from '@/components/landing/Editorial'
import type { IndustryData } from '@/data/industries'
import { lowerFirst } from './listing'

const OUT_EASE = [0.22, 1, 0.36, 1] as const

const MARKS: Array<{ geo: GeoVariant; color: 'blue' | 'purple' | 'pink' }> = [
  { geo: 'ring', color: 'purple' },
  { geo: 'bars', color: 'pink' },
  { geo: 'chevrons', color: 'blue' },
  { geo: 'cube', color: 'purple' },
]

/**
 * The reference's rotated process cards, re-cut for an industry's pain
 * points (usually three): white bordered cards overlapping in a zig-zag,
 * rotated −5° / +5° in turn, the even ones raised 10rem. Each card states
 * the problem, what it costs today and what changes. On hover a card
 * straightens and comes forward. Grid with a ±2° tilt below lg; static on
 * phones.
 */
export function RotatedPainCards({ industry, className }: { industry: IndustryData; className?: string }) {
  const compact = useCompactViewport()
  const cards = industry.painPoints
  const width = cards.length >= 4 ? 'lg:w-[calc(25%+1.125rem)]' : 'lg:w-[calc(33.333%+1rem)]'

  return (
    <ol className={cn('grid gap-6 sm:grid-cols-2 lg:flex lg:gap-0 lg:pt-40', className)}>
      {cards.map((card, i) => {
        const mark = MARKS[i % MARKS.length]!
        const odd = i % 2 === 1
        return (
          <motion.li
            key={card.title}
            className={cn('relative hover:z-10 lg:-ml-6 lg:shrink-0 lg:first:ml-0', width)}
            initial={compact ? false : { opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '0px 0px -10% 0px' }}
            transition={{ duration: 0.8, ease: OUT_EASE, delay: i * 0.1 }}
          >
            <div
              className={cn(
                'flex h-full flex-col justify-between gap-10 border border-[var(--color-landing-gray-200)] bg-white p-7 transition-[rotate,translate,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] dark:border-[var(--color-landing-gray-800)] dark:bg-[#12121a] md:p-9 lg:min-h-[26rem] lg:pr-12',
                odd ? 'rotate-[2deg] lg:-translate-y-40 lg:rotate-[5deg]' : 'rotate-[-2deg] lg:rotate-[-5deg]',
                'hover:rotate-0 hover:shadow-[0_30px_60px_-30px_rgba(10,10,10,0.35)]'
              )}
            >
              <GeoMark variant={mark.geo} color={mark.color} className="h-12 w-12 shrink-0 md:h-14 md:w-14" />
              <div>
                <h3 className="font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white md:text-xl">
                  {card.title}
                </h3>
                <p className="landing-text-body-sm mt-4 text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]">
                  Today: {lowerFirst(card.impact)}.
                </p>
                <p className="mt-4 flex items-start gap-2 text-base font-semibold text-[var(--color-landing-gray-900)] dark:text-white">
                  <ArrowRight aria-hidden className="mt-1 h-4 w-4 shrink-0 text-[var(--color-landing-accent)]" />
                  {card.solution}
                </p>
              </div>
            </div>
          </motion.li>
        )
      })}
    </ol>
  )
}
